import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import SETTINGS from "../../globals";
import AppSections from "../../utils/AppSections";
import { useApiClient } from "../../contexts/ApiClientContext";
import PagedData from "../../model/PagedData";
import APIEndPoints from "../../utils/APIEndPoints";
import Loading from "../common/Loading";
import Button from "../common/button/Button";
import ButtonAction from "../common/button/ButtonAction";
import AnnexDataListElement from "./AnnexDataListElement";
import AnnexData from "../../model/AnnexData";
import UserRoles from "../../model/enums/UserRole";
import ButtonType from "../common/button/ButtonType";
import EmbeddingToolInfo from "../../utils/EmbeddingToolInfo";

interface IAnnexDataListProps {
  id_chelement: number | undefined;
  id_site: number | undefined;
  id_e3dmodel: number | undefined;
}

/*
  Component to manage the display of the ANNEX DATA lists
  We can have ANNEX DATA for SITE or CHELEMENET
*/
function AnnexDataList({
  id_chelement,
  id_site,
  id_e3dmodel,
}: IAnnexDataListProps) {
  // reference to the AuthManager
  const authManager = useAuth();
  // reference to the navigator
  const navigate = useNavigate();
  // reference to the ApiClient instance
  const apiClient = useApiClient();
  const [filteredData, setFilteredData] = useState<AnnexData[]>([]);
  const [showMore, setShowMore] = useState(false);
  const [section, setSection] = useState<string>("all");
  // manage data loading animation
  const [loading, setLoading] = useState(true);

  // load record data from the server
  useEffect(() => {
    apiClient
      ?.get<PagedData<AnnexData[]>>({
        url: APIEndPoints.ANNEXDATA.ALL(id_chelement, id_site, id_e3dmodel),
        requiresToken: true,
      })
      .then((response: PagedData<AnnexData[]>) => {
        console.log("Set Data filter: " + section);
        const items = response.items;
        // setData(items);

        let filteredItems = items;
        if (section !== "all")
          filteredItems = items.filter((item) => item.category === section);

        setFilteredData(filteredItems.slice(0, 4));
        setShowMore(filteredItems.length > 4);
      })
      .catch(() => {
        navigate(SETTINGS.get_path({ section: AppSections.SIGN_IN }));
      })
      .finally(() => {
        setLoading(false);
      });
  }, [apiClient, id_chelement, id_site, section, navigate]);

  const handleShowMore = () => {
    apiClient
      ?.get<PagedData<AnnexData[]>>({
        url: APIEndPoints.ANNEXDATA.ALL(id_chelement, id_site, id_e3dmodel),
        requiresToken: true,
      })
      .then((response: PagedData<AnnexData[]>) => {
        // setData(response.items);

        // setDataFilter(section)
        let filteredItems = response.items;
        if (section !== "all")
          filteredItems = response.items.filter(
            (item) => item.category === section,
          );

        setFilteredData(filteredItems);

        setShowMore(false);
      })
      .catch(() => {
        navigate(SETTINGS.get_path({ section: AppSections.SIGN_IN }));
      })
      .finally(() => {
        setLoading(false);
      });
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <div>
      {false && (
        <div className="inline-flex text-sm mb-5 w-full gap-x-5 justify-around">
          <Button
            className="ml-0 w-1/4"
            label="All"
            purpose={ButtonAction.UPDATE}
            type={ButtonType.FILL_TEXT}
            onClick={() => setSection("all")}
            activeState={section === "all" ? 1 : 2} // This will be true by default
          />
          <Button
            className="ml-0 w-1/4"
            label="Site Inspection"
            purpose={ButtonAction.UPDATE}
            type={ButtonType.FILL_TEXT}
            onClick={() => setSection("site_inspection")}
            activeState={section === "site_inspection" ? 1 : 2}
          />
          <Button
            className="ml-0 w-1/4"
            label="Historical Data"
            purpose={ButtonAction.UPDATE}
            type={ButtonType.FILL_TEXT}
            onClick={() => {
              setSection("historical_data");
            }}
            activeState={section === "historical_data" ? 1 : 2}
          />
          <Button
            className="ml-0 w-1/4"
            label="Survey Data"
            purpose={ButtonAction.UPDATE}
            type={ButtonType.FILL_TEXT}
            onClick={() => {
              setSection("survey_data");
            }}
            activeState={section === "survey_data" ? 1 : 2}
          />
        </div>
      )}

      <div className="grid grid-cols-4 gap-6">
        {filteredData?.length > 0 &&
          filteredData?.map((item) => {
            const canEdit =
              (item.id_user === authManager.getUserID() ||
                authManager.getUserRole() === UserRoles.ADMIN) &&
              !EmbeddingToolInfo.isChemiTool();
            return (
              <AnnexDataListElement
                data={item}
                key={item.id}
                canEdit={canEdit}
              />
            );
          })}

        {showMore && (
          <div className="details-show-more">
            <Button
              label="Show all documents"
              onClick={handleShowMore}
              purpose={ButtonAction.UPDATE}
            />
          </div>
        )}

        {filteredData?.length === 0 && (
          <div className="details-no-records">No documents available.</div>
        )}
      </div>
    </div>
  );
}

export default AnnexDataList;
